import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Shared Hero Image component for all 13 archetype templates.
 * Supports single image or auto-rotating carousel.
 * Falls back to a color gradient when no hero images exist.
 *
 * Props:
 *   images    — array of image URLs (from association.hero_images)
 *   overlay   — CSS background string for the overlay (gradient)
 *   className — additional CSS classes for the container
 *   interval  — auto-rotate interval in ms (default 6000, 0 to disable)
 *   children  — overlay content (title, badges, etc.)
 */
const HeroCarousel = ({ images = [], overlay, className = '', interval = 6000, children }) => {
  const [current, setCurrent] = useState(0);
  const hasImages = images.length > 0;
  const multi = images.length > 1;

  const next = useCallback(() => {
    setCurrent(i => (i + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    setCurrent(i => (i - 1 + images.length) % images.length);
  }, [images.length]);

  // Auto-rotate
  useEffect(() => {
    if (!multi || interval <= 0) return;
    const timer = setInterval(next, interval);
    return () => clearInterval(timer);
  }, [multi, interval, next]);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Image layer */}
      {hasImages && images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === current ? 1 : 0 }}
          loading={i === 0 ? 'eager' : 'lazy'}
          data-testid={i === 0 ? 'hero-image' : undefined}
        />
      ))}

      {/* Overlay */}
      {overlay && <div className="absolute inset-0" style={{ background: overlay }} />}

      {/* Content */}
      <div className="relative z-10">{children}</div>

      {/* Carousel controls — only when multiple images */}
      {multi && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center backdrop-blur-sm transition-colors"
            data-testid="hero-prev"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center backdrop-blur-sm transition-colors"
            data-testid="hero-next"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="w-2 h-2 rounded-full transition-all"
                style={{
                  backgroundColor: i === current ? 'white' : 'rgba(255,255,255,0.4)',
                  transform: i === current ? 'scale(1.3)' : 'scale(1)',
                }}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroCarousel;
